<template>
  <div class="mask" @mousedown.self="onCancel">
    <div class="add-dialog">
      <!-- ヘッダー -->
      <div class="add-header">
        <span v-if="mode !== 'freeLimit'" class="add-header-title">
          {{
            mode === "premiumLimit"
              ? $t("homePage.novelList.grouping.premiumLimitTitle")
              : $t("homePage.novelList.grouping.addGroup")
          }}
        </span>
        <button class="add-close-button" @click="onCancel">
          <IconClose class="add-close-icon" />
        </button>
      </div>

      <!-- 無料プラン上限到達時 -->
      <template v-if="mode === 'freeLimit'">
        <div class="add-limit-body">
          <img :src="mascotImageSrc" alt="" class="add-limit-mascot" />
          <h3 class="add-limit-title">{{ $t("homePage.novelList.grouping.freeLimitTitle") }}</h3>
          <p class="add-limit-description">{{ $t("homePage.novelList.grouping.freeLimitMessage") }}</p>
        </div>
        <div class="add-actions">
          <button class="add-premium-button" @click="onClickPremium">
            {{ $t("homePage.novelList.grouping.becomePremium") }}
          </button>
          <button class="add-cancel-button" @click="onCancel">
            {{ $t("common.close") }}
          </button>
        </div>
      </template>

      <!-- プレミアムプラン上限到達時 -->
      <template v-else-if="mode === 'premiumLimit'">
        <div class="add-limit-message">
          <p>{{ $t("homePage.novelList.grouping.premiumLimitMessage") }}</p>
        </div>
        <div class="add-actions">
          <button class="add-cancel-button" @click="onCancel">
            {{ $t("common.close") }}
          </button>
        </div>
      </template>

      <!-- 通常時 -->
      <template v-else>
        <div class="add-body">
          <div class="add-label-row">
            <span class="add-label">{{ $t("homePage.novelList.grouping.groupName") }}</span>
            <span class="add-char-count" :class="{ over: isOverLimit }">{{ charCount }}/50</span>
          </div>
          <input
            v-model="groupName"
            class="add-input"
            type="text"
            :placeholder="$t('homePage.novelList.grouping.groupNamePlaceholder')"
            :maxlength="GROUP_NAME_MAX_LENGTH"
          />
        </div>

        <div class="add-actions">
          <button class="add-submit-button" :disabled="!canSubmit" @click="onSubmit">
            {{ $t("homePage.novelList.grouping.add") }}
          </button>
          <button class="add-cancel-button" @click="onCancel">
            {{ $t("common.cancel") }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { NovelGroup, Plan } from "@/lib/models";
import IconClose from "~icons/figma/Close";

const GROUP_NAME_MAX_LENGTH = 50;
const GROUP_LIMIT_FREE = 2;
const GROUP_LIMIT_PREMIUM = 1000;

export const DIALOG_RESULT_PREMIUM = "premium" as const;

export default Vue.extend<Data, Methods, Computed, {}>({
  components: { IconClose },
  data() {
    const groups = this.$store.getters["novelGroupModule/groups"] as NovelGroup[];
    const isPremium = this.$store.getters["billingModule/plan"] !== Plan.free;
    const limit = isPremium ? GROUP_LIMIT_PREMIUM : GROUP_LIMIT_FREE;
    const isLimitReached = groups.length >= limit;

    let mode: DialogMode = "create";
    if (isLimitReached) {
      mode = isPremium ? "premiumLimit" : "freeLimit";
    }

    return {
      groupName: "",
      mode,
    };
  },

  computed: {
    GROUP_NAME_MAX_LENGTH() {
      return GROUP_NAME_MAX_LENGTH;
    },
    charCount() {
      return this.groupName.length;
    },
    isOverLimit() {
      return this.groupName.length > GROUP_NAME_MAX_LENGTH;
    },
    canSubmit() {
      return this.groupName.trim().length > 0 && !this.isOverLimit;
    },
    mascotImageSrc() {
      const baseUrl = process.env.VUE_APP_NOLA_WEB;
      return `${baseUrl}/writer-type-indicator/images/result/cats/emps.png`;
    },
  },

  methods: {
    async onSubmit() {
      if (!this.canSubmit) return;

      try {
        const group = await this.$store.dispatch("novelGroupModule/createGroup", {
          name: this.groupName.trim(),
        });
        this.$close(group);
      } catch (error) {
        // API側のバリデーションエラーはGraphQLClientが処理する
      }
    },

    onClickPremium() {
      this.$router.push({ name: "subscriptionAnnounce" });
      this.$close(DIALOG_RESULT_PREMIUM);
    },
    onCancel() {
      this.$close(null);
    },
  },
});

type DialogMode = "create" | "freeLimit" | "premiumLimit";

interface Data {
  groupName: string;
  mode: DialogMode;
}

interface Computed {
  GROUP_NAME_MAX_LENGTH: number;
  mascotImageSrc: string;
  charCount: number;
  isOverLimit: boolean;
  canSubmit: boolean;
}

interface Methods {
  onSubmit(): Promise<void>;
  onClickPremium(): void;
  onCancel(): void;
}
</script>

<style lang="scss" scoped>
.mask {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  z-index: 8001;
  align-items: center;
  justify-content: center;
  background-color: var(--color-overlay);
}

.add-dialog {
  background-color: var(--color-background-normal);
  border-radius: var(--radius-md);
  width: 340px;
  max-width: 90%;
  display: flex;
  flex-direction: column;
}

/* ヘッダー */
.add-header {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 20px 24px 12px;
}

.add-header-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
}

.add-close-button {
  position: absolute;
  right: 16px;
  top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
}

.add-close-icon {
  font-size: var(--font-size-sm);
  color: var(--color-text-normal);
}

/* 無料プラン上限（プレミアム誘導） */
.add-limit-body {
  padding: 16px;
  text-align: center;
}

.add-limit-mascot {
  width: 120px;
  height: auto;
  margin-bottom: 12px;
}

.add-limit-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  margin: 0 0 12px;
}

.add-limit-description {
  font-size: var(--font-size-sm);
  color: var(--color-text-normal);
  margin: 0;
  line-height: var(--line-height-default);
  text-align: left;
  white-space: pre-line;
}

.add-premium-button {
  width: 100%;
  padding: 10px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-on-fixed-bg);
  background-color: var(--color-primary);
  border: none;
  border-radius: var(--radius-rounded);
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.85;
  }
}

/* プレミアムプラン上限メッセージ */
.add-limit-message {
  padding: 16px 24px 8px;
  text-align: left;

  p {
    font-size: var(--font-size-sm);
    color: var(--color-text-normal);
    margin: 0;
    line-height: var(--line-height-default);
    white-space: pre-line;
  }
}

/* 入力部 */
.add-body {
  padding: 8px 24px 0;
}

.add-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.add-label {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
}

.add-char-count {
  font-size: var(--font-size-xs);
  color: var(--color-text-low);

  &.over {
    color: var(--color-warning);
  }
}

.add-input {
  width: 100%;
  padding: 10px 12px;
  font-size: var(--font-size-sm);
  color: var(--color-text-normal);
  background-color: var(--color-surface-muted);
  border: none;
  border-radius: var(--radius-sm);
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: var(--color-inactive);
  }
}

/* アクション */
.add-actions {
  padding: 12px 24px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.add-submit-button {
  width: 100%;
  padding: 10px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-on-fixed-bg);
  background-color: var(--color-primary);
  border: none;
  border-radius: var(--radius-rounded);
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.add-cancel-button {
  width: 100%;
  padding: 10px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  background-color: var(--color-surface-muted);
  border: none;
  border-radius: var(--radius-rounded);
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.85;
  }
}
</style>
